import React, { useContext, useState, useEffect } from 'react';
import { DataContext } from '../contexts/DataContext';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCsv, faSearch, faSpinner, faPlus, faEdit, faFileInvoiceDollar, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../utils/Pagination';
import ExportCSVButton from '../utils/ExportCSVButton';
import logo from '../wisdom.png';
import { useNavigate } from 'react-router-dom';
import PaymentAdvanceModal from './payment_advance/PaymentAdvanceModal';
import PaymentRequestModal from './payment_request/PaymentRequestModal';

const TrafficDataGrid = () => {
    const { data, fetchData, isLoading, referencesWithPA, referencesWithPR } = useContext(DataContext);

    const [filteredData, setFilteredData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [pageSize, setPageSize] = useState(100);
    const [currentPage, setCurrentPage] = useState(1);
    const [trafficReferenceOptions, setTrafficReferenceOptions] = useState([]);
    const [entryPointOptions, setEntryPointOptions] = useState([]);
    const [customerNameOptions, setCustomerNameOptions] = useState([]);
    const [selectedTrafficReferences, setSelectedTrafficReferences] = useState([]);
    const [selectedEntryPoints, setSelectedEntryPoints] = useState([]);
    const [selectedCustomerNames, setSelectedCustomerNames] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'dates_pedimento.paid_date', direction: 'descending' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPaymentDetails, setSelectedPaymentDetails] = useState(null);
    const [showOnlyWithPA, setShowOnlyWithPA] = useState(false);
    const [showOnlyWithPR, setShowOnlyWithPR] = useState(false);
    const [isPRModalOpen, setIsPRModalOpen] = useState(false);
    const [selectedPRDetails, setSelectedPRDetails] = useState([]);
    const [currentRowData, setCurrentRowData] = useState(null);
    const [modalDisplayType, setModalDisplayType] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (data) {
            // Extract unique options for each filter category
            setTrafficReferenceOptions([...new Set(data.map(item => item.traffic_reference))].map(option => ({ value: option, label: option })));
            setEntryPointOptions([...new Set(data.map(item => item.entry_point))].map(option => ({ value: option, label: option })));
            setCustomerNameOptions([...new Set(data.map(item => item.customer_data.customer_name))].map(option => ({ value: option, label: option })));

            filterData();
        }
    }, [data]);

    useEffect(() => {
        filterData();
    }, [showOnlyWithPA]);

    useEffect(() => {
        filterData();
    }, [showOnlyWithPR]);

    const handlePaymentAdvanceClick = (paymentDetails) => {
        setSelectedPaymentDetails(paymentDetails);
        setIsModalOpen(true);
        setModalDisplayType('click');
    };

    const handleSort = (path) => {
        sortData(path);
    };

    const getValueByPath = (path, obj) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    };

    const sortData = (path) => {
        let direction = 'ascending';
        if (sortConfig.key === path && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key: path, direction });

        const sortedData = [...filteredData].sort((a, b) => {
            let aValue = getValueByPath(path, a);
            let bValue = getValueByPath(path, b);

            // Special handling for dates
            if (path === 'dates_pedimento.paid_date') {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }

            if (aValue < bValue) {
                return direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
                return direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });

        setFilteredData(sortedData);
    };


    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        filterData(e.target.value, selectedTrafficReferences, selectedEntryPoints, selectedCustomerNames);
    };

    const handleSearchClick = () => {
        filterData(searchTerm, selectedTrafficReferences, selectedEntryPoints, selectedCustomerNames);
    };

    const handleFilterChange = () => {
        filterData(searchTerm, selectedTrafficReferences, selectedEntryPoints, selectedCustomerNames);
    };

    const handleAddPA = (row) => {
        if (!(row.traffic_reference in referencesWithPA)) {
            navigate('/paymentAdvance', { state: { rowData: row } });
        }
    };

    const handleEditPA = (row) => {
        if (row.paymentAdvanceDetails && row.paymentAdvanceDetails._id) {
            navigate('/paymentAdvance', { state: { rowData: row, paymentAdvanceId: row.paymentAdvanceDetails._id } });
        }
    };

    const updateOptionsBasedOnFilteredData = (filtered) => {
        setTrafficReferenceOptions([...new Set(filtered.map(item => item.traffic_reference))].map(option => ({ value: option, label: option })));
        setEntryPointOptions([...new Set(filtered.map(item => item.entry_point))].map(option => ({ value: option, label: option })));
        setCustomerNameOptions([...new Set(filtered.map(item => item.customer_data.customer_name))].map(option => ({ value: option, label: option })));
    };

    const filterData = () => {
        let filtered = data || [];
        const searchLowerCase = searchTerm.toLowerCase();

        if (selectedTrafficReferences.length) {
            filtered = filtered.filter(item => selectedTrafficReferences.includes(item.traffic_reference));
        }
        if (selectedEntryPoints.length) {
            filtered = filtered.filter(item => selectedEntryPoints.includes(item.entry_point));
        }
        if (selectedCustomerNames.length) {
            filtered = filtered.filter(item => selectedCustomerNames.includes(item.customer_data.customer_name));
        }

        if (searchTerm) {
            filtered = filtered.filter(item =>
                item.traffic_reference?.toLowerCase().includes(searchLowerCase) ||
                item.entry_point?.toLowerCase().includes(searchLowerCase) ||
                item.customer_data?.customer_name?.toLowerCase().includes(searchLowerCase) ||
                item.license?.toLowerCase().includes(searchLowerCase) ||
                item.pedimento?.toLowerCase().includes(searchLowerCase) ||
                item.customer_data?.customer_rfc?.toLowerCase().includes(searchLowerCase) ||
                item.dates_pedimento?.paid_date?.toLowerCase().includes(searchLowerCase) ||
                item.status_modulacion?.toLowerCase().includes(searchLowerCase) ||
                item.tipo_pedimento?.toLowerCase().includes(searchLowerCase) ||
                item.status_pedimento?.toLowerCase().includes(searchLowerCase)
            );
        }

        // First, update the records with the payment advance information
        const updatedDataWithPAandPR = filtered.map(item => ({
            ...item,
            hasPaymentAdvance: item.traffic_reference in referencesWithPA,
            paymentAdvanceDetails: referencesWithPA[item.traffic_reference] || null,
            hasPaymentRequest: item.traffic_reference in referencesWithPR, // Check if the traffic_reference is a key in referencesWithPR
            paymentRequestDetails: referencesWithPR[item.traffic_reference] || [] // Use the traffic_reference to get the payment request details or default to an empty array
        }));


        // Then, if showOnlyWithPA is true, filter out those without a payment advance
        if (showOnlyWithPA) {
            filtered = updatedDataWithPAandPR.filter(item => item.hasPaymentAdvance);
        } else if (showOnlyWithPR) {
            filtered = updatedDataWithPAandPR.filter(item => item.hasPaymentRequest);
        } else {
            filtered = updatedDataWithPAandPR;
        }

        updateOptionsBasedOnFilteredData(filtered);
        setFilteredData(filtered);
    };

    useEffect(() => {
        filterData();
    }, [searchTerm, selectedTrafficReferences, selectedEntryPoints, selectedCustomerNames, data, showOnlyWithPA, referencesWithPA]);


    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(1);
    };

    const paginatedData = filteredData.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    useEffect(() => {
        if (data && referencesWithPA) {
            const mergedData = data.map(item => ({
                ...item,
                hasPaymentAdvance: item.traffic_reference in referencesWithPA,
                paymentAdvanceDetails: referencesWithPA[item.traffic_reference] || null
            }));
            setFilteredData(mergedData);
            filterData();
        }
    }, [data, referencesWithPA]);

    const getRojoVerdeStyle = (value) => {
        switch (value) {
            case 'V':
                return "bg-green-600 text-green-200";
            case 'R':
                return "bg-red-600 text-red-200";
            default:
                return "";
        }
    };

    const handleAddPR = (item) => {
        const paymentRequests = referencesWithPR[item.traffic_reference] || [];
        setSelectedPRDetails(paymentRequests);
        setCurrentRowData(item);
        setIsPRModalOpen(true);
    };

    const handleNavigateToPaymentRequest = (paymentRequestData, trafficReference) => {
        if (paymentRequestData && trafficReference) {
            const associatedRowData = filteredData.find(item => item.traffic_reference === trafficReference);
            if (associatedRowData) {
                navigate('/paymentRequest', {
                    state: {
                        rowData: {
                            ...associatedRowData,
                            paymentRequestData
                        },
                        paymentRequestId: paymentRequestData._id // Add this line
                    }
                });
            }
        } else {
            // Handle the case for adding a new payment request
            navigate('/paymentRequest', { state: { rowData: currentRowData } });
        }
    };



    const getStatusPedimentoStyle = (value) => {
        switch (value) {
            case 'PAGADO':
                return "bg-green-600 text-green-200";
            case 'ABIERTO':
                return "bg-yellow-600 text-yellow-200";
            default:
                return "";
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <FontAwesomeIcon icon={faSpinner} className="text-xl font-semibold animate-spin" />
                <div className="text-xl font-semibold ml-2">Loading...</div>
            </div>
        );
    }

    return (
        <>
            <nav className="w-full flex justify-between items-center px-6 py-4 bg-white shadow">
                <div className="flex items-center">
                    <img src={logo} alt="Wisdom logo" className="h-12 mr-2" />
                </div>
                <div className="text-xl font-semibold text-gray-700">
                    Payment Advance Management - Traffic
                </div>
            </nav>
            <div className="container mx-auto p-6 bg-gray-100">
                <div className="mb-4 grid grid-cols-1 md:grid-cols-6 gap-4">
                    <Select
                        options={trafficReferenceOptions}
                        isMulti
                        onChange={opts => { setSelectedTrafficReferences(opts.map(o => o.value)); handleFilterChange(); }}
                        className="mb-4"
                        placeholder="Select Traffic References"
                        classNamePrefix="select"
                    />
                    <Select
                        options={entryPointOptions}
                        isMulti
                        onChange={opts => { setSelectedEntryPoints(opts.map(o => o.value)); handleFilterChange(); }}
                        className="mb-4"
                        placeholder="Select Entry Points"
                        classNamePrefix="select"
                    />
                    <Select
                        options={customerNameOptions}
                        isMulti
                        onChange={opts => { setSelectedCustomerNames(opts.map(o => o.value)); handleFilterChange(); }}
                        className="mb-4"
                        placeholder="Select Customer Names"
                        classNamePrefix="select"
                    />
                    <div className="flex items-center">
                        <input
                            id="show-only-pa"
                            type="checkbox"
                            className="mr-2"
                            checked={showOnlyWithPA}
                            onChange={e => setShowOnlyWithPA(e.target.checked)}
                        />
                        <label htmlFor="show-only-pa">Show only records with Payment Advance</label>
                    </div>
                    <div className="flex items-center">
                        <input
                            id="show-only-pr"
                            type="checkbox"
                            className="mr-2"
                            checked={showOnlyWithPR}
                            onChange={e => setShowOnlyWithPR(e.target.checked)}
                        />
                        <label htmlFor="show-only-pr">Show only records with Payment Request</label>
                    </div>
                    <div className="flex justify-end">
                        <ExportCSVButton data={filteredData} filename="traffic_data.csv" />
                    </div>
                </div>
                <div className="mb-4">
                    <div className="flex items-center p-2 rounded">
                        <FontAwesomeIcon icon={faSearch} className="text-gray-600 mr-2" />
                        <input
                            type="text"
                            placeholder="Search..."
                            className="p-2 border rounded flex-grow"
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                        <button
                            onClick={handleSearchClick} // Updated to call handleSearchClick
                            className="ml-2 px-4 py-2 text-lg bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none" // Added ml-2 for margin-left
                        >
                            Search
                        </button>
                    </div>
                </div>
                <Pagination
                    total={filteredData.length}
                    pageSize={pageSize}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
                <div className="overflow-x-auto">
                    <div className="bg-gray-50">
                        <div className="grid grid-cols-12 gap-4 p-4 border-b-2 border-gray-200">
                            <div className="text-center uppercase font-semibold text-sm cursor-pointer"
                                onClick={() => handleSort('traffic_reference')}>
                                Traffic Reference
                                {/* <SortIcon /> - Implement if needed */}
                            </div>
                            <div className="text-center uppercase font-semibold text-sm cursor-pointer"
                                onClick={() => handleSort('entry_point')}>
                                Entry Point
                                {/* <SortIcon /> - Implement if needed */}
                            </div>
                            <div className="text-center uppercase font-semibold text-sm cursor-pointer"
                                onClick={() => handleSort('license')}>
                                License
                                {/* <SortIcon /> - Implement if needed */}
                            </div>
                            <div className="text-center uppercase font-semibold text-sm cursor-pointer"
                                onClick={() => handleSort('pedimento')}>
                                Pedimento
                                {/* <SortIcon /> - Implement if needed */}
                            </div>
                            <div className="text-center uppercase font-semibold text-sm cursor-pointer"
                                onClick={() => handleSort('customer_data.customer_name')}>
                                Customer Name
                                {/* <SortIcon /> - Implement if needed */}
                            </div>
                            <div className="text-center uppercase font-semibold text-sm cursor-pointer"
                                onClick={() => handleSort('customer_data.customer_rfc')}>
                                RFC
                                {/* <SortIcon /> - Implement if needed */}
                            </div>
                            <div className="text-center uppercase font-semibold text-sm cursor-pointer"
                                onClick={() => handleSort('dates_pedimento.paid_date')}>
                                Date Paid
                                {/* <SortIcon /> - Implement if needed */}
                            </div>

                            <div className="text-center uppercase font-semibold text-sm cursor-pointer"
                                onClick={() => handleSort('status_modulacion')}>
                                Status Crossed
                                {/* <SortIcon /> - Implement if needed */}
                            </div>
                            <div className="text-center uppercase font-semibold text-sm cursor-pointer"
                                onClick={() => handleSort('tipo_pedimento')}>
                                Document Type
                                {/* <SortIcon /> - Implement if needed */}
                            </div>
                            <div className="text-center uppercase font-semibold text-sm cursor-pointer"
                                onClick={() => handleSort('status_pedimento')}>
                                Status Pedimento
                                {/* <SortIcon /> - Implement if needed */}
                            </div>
                            <div className="text-center uppercase font-semibold text-sm cursor-pointer"
                                onClick={() => handleSort('status_pedimento')}>
                                Payment Advance
                                {/* <SortIcon /> - Implement if needed */}
                            </div>
                            <div className="text-center uppercase font-semibold text-sm cursor-pointer"
                            >
                                Actions
                                {/* <SortIcon /> - Implement if needed */}
                            </div>
                            {/* ... additional headers ... */}
                        </div>
                    </div>

                    {/* Data Rows */}
                    {paginatedData.map((item, index) => {

                        const paymentAdvanceDetails = item.paymentAdvanceDetails
                            ? `Taxes: $${new Intl.NumberFormat('en-US').format(item.paymentAdvanceDetails.total_taxes)}, ` +
                            `Expenses: $${new Intl.NumberFormat('en-US').format(item.paymentAdvanceDetails.total_actual_expenses)}, ` +
                            `Brokerage: $${new Intl.NumberFormat('en-US').format(item.paymentAdvanceDetails.total_custom_brokerage)}`
                            : 'N/A';


                        return (
                            <div key={index} className="grid grid-cols-12 gap-4 p-4 bg-white m-2 rounded shadow">
                                <div className="flex items-center justify-center">{item.traffic_reference}</div>
                                <div className="flex items-center justify-center">
                                    <span className="inline-block bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-xs font-semibold" style={{ width: '50px' }}>
                                        {item.entry_point}
                                    </span>
                                </div>
                                <div className="flex items-center justify-center">
                                    <span className="inline-block bg-yellow-100 text-yellow-800 rounded-full px-3 py-1 text-xs font-semibold" style={{ width: '50px' }}>
                                        {item.license}
                                    </span></div>
                                <div className="flex items-center justify-center">
                                    <span className="inline-block bg-purple-100 text-purple-800 rounded-full px-3 py-1 text-xs font-semibold" style={{ width: '75px' }}>
                                        {item.pedimento}
                                    </span></div>
                                <div className="flex items-center justify-center">{item.customer_data.customer_name}</div>
                                <div className="flex items-center justify-center">{item.customer_data.customer_rfc}</div>
                                <div className="flex items-center justify-center">
                                    <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold bg-purple-500 text-purple-200`}>
                                        {item.dates_pedimento.paid_date}
                                    </span>
                                </div>
                                <div className="flex items-center justify-center">
                                    <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${getRojoVerdeStyle(item.status_modulacion)}`}>
                                        {item.status_modulacion}
                                    </span>
                                </div>
                                <div className="flex items-center justify-center">{item.tipo_pedimento}</div>
                                <div className="flex items-center justify-center">
                                    <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${getStatusPedimentoStyle(item.status_pedimento)}`}>
                                        {item.status_pedimento}
                                    </span>
                                </div>
                                <div className="flex items-center justify-center">
                                    <div className="flex items-center justify-center">
                                        {item.paymentAdvanceDetails ? (
                                            <button
                                                onClick={() => handlePaymentAdvanceClick(item.paymentAdvanceDetails)}
                                                className={`font-bold p-1 rounded-lg text-sm hover:bg-blue-600 transition-colors ${item.paymentAdvanceDetails.status === 'open' ? 'bg-blue-500 text-white' :
                                                    item.paymentAdvanceDetails.status === 'review' ? 'bg-yellow-500 text-white' :
                                                        item.paymentAdvanceDetails.status === 'closed' ? 'bg-green-500 text-white' :
                                                            item.paymentAdvanceDetails.status === 'advance_received' ? 'bg-purple-500 text-white' :
                                                                item.paymentAdvanceDetails.status === 'documents_closed' ? 'bg-orange-500 text-white' : ''

                                                    }`}
                                                title="View Payment Advance"
                                            >
                                                PA
                                            </button>
                                        ) : 'N/A'}
                                    </div>
                                </div>
                                <div className="flex items-center justify-center col-span-12 lg:col-span-3 xl:col-span-2 2xl:col-span-1 space-x-2">
                                    <div className="flex flex-col space-y-2">
                                        <div className="flex items-center justify-center col-span-12 lg:col-span-3 xl:col-span-2 2xl:col-span-1 space-x-2">
                                            <button
                                                className="p-1 text-blue-500 hover:text-blue-600 bg-transparent rounded focus:outline-none"
                                                onClick={() => handleAddPA(item)}
                                                disabled={item.hasPaymentAdvance}
                                                title="Add Payment Advance"
                                            >
                                                <FontAwesomeIcon icon={faPlus} size="lg" />
                                            </button>
                                            <button
                                                className="p-1 text-blue-500 hover:text-blue-600 bg-transparent rounded focus:outline-none"
                                                onClick={() => handleEditPA(item)}
                                                disabled={!item.hasPaymentAdvance} // Disable if no payment advance ID
                                                title="Edit Payment Advance"
                                            >
                                                <FontAwesomeIcon icon={faEdit} size="lg" />
                                            </button>
                                            <button
                                                onClick={() => handleAddPR(item)}
                                                className="p-1 text-purple-800 hover:text-purple-900 bg-purple-200 rounded hover:bg-purple-300 focus:outline-none"
                                                title="Add Payment Request"
                                            >
                                                <FontAwesomeIcon icon={faFileInvoiceDollar} size="lg" />
                                            </button>
                                        </div>
                                    </div>
                                </div>


                            </div>
                        );
                    })}
                    <PaymentAdvanceModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        paymentDetails={selectedPaymentDetails || {}}
                    />
                    <PaymentRequestModal
                        isOpen={isPRModalOpen}
                        onClose={() => setIsPRModalOpen(false)}
                        paymentRequestDetails={selectedPRDetails}
                        onNavigateToPaymentRequest={handleNavigateToPaymentRequest}
                        onAddNewPaymentRequest={() => handleNavigateToPaymentRequest(null, currentRowData?.traffic_reference)}
                        trafficReference={currentRowData?.traffic_reference || 'defaultTrafficReference'} // Add a fallback or ensure currentRowData is not null
                    />


                </div>
            </div >
        </>
    );
};

export default TrafficDataGrid;
